import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { RootState, AppDispatch } from '../store';
import { registerAsync, googleAuthAsync, clearError } from '../store/authSlice';
import { RegisterRequest } from '../types';
import { trackFunnel } from '../utils/tracking';
import { getPendingCoupon } from '../utils/pendingCoupon';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import {
  AUTH_TESTIMONIALS,
  AUTH_SUBTITLE,
  authBgUrl,
  AUTH_BG_UNSPLASH_IDS,
} from '../config/authTestimonials';
import {
  BrandMark,
  CREAM,
  DottedRule,
  HighlightedWord,
  INDIGO,
  INDIGO_DARK,
  INK,
  INK_SOFT,
  INTER_STACK,
  LeftPanel,
  MONO_STACK,
  SkuKicker,
  YELLOW,
} from '../components/auth/TicketTape';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const reducedMotion = useReducedMotion() ?? false;
  const { loading, error, isAuthenticated, tenant } = useSelector(
    (state: RootState) => state.auth,
  );

  // 'main' = paso inicial (Google + email form)
  // 'google-company' = nombre del negocio después de Google auth
  const [step, setStep] = useState<'main' | 'google-company'>('main');
  const [pendingGoogleToken, setPendingGoogleToken] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [localError, setLocalError] = useState('');

  const subdomain = useMemo(() => slugify(companyName), [companyName]);
  // Mientras se registra, la navegación la hace el submit después de mandar los eventos del embudo.
  const signingUp = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || signingUp.current) return;
    if (tenant && !tenant.onboardingCompletedAt) {
      navigate('/completar-perfil');
    } else {
      navigate('/dashboard');
    }
  }, [isAuthenticated, tenant, navigate]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const validate = (): string | null => {
    if (!companyName.trim()) return 'Ingresá el nombre de tu negocio';
    if (!email.trim()) return 'Ingresá tu email';
    if (!password) return 'Ingresá una contraseña';
    if (password.length < 6) return 'La contraseña debe tener al menos 6 caracteres';
    if (!subdomain) return 'El nombre del negocio debe tener letras o números';
    if (!acceptedTerms) return 'Tenés que aceptar los términos y condiciones';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    const err = validate();
    if (err) {
      setLocalError(err);
      return;
    }
    const payload: RegisterRequest = {
      name: companyName.trim(),
      email: email.trim(),
      password,
      companyName: companyName.trim(),
      subdomain,
      plan: 'Growth',
      couponCode: getPendingCoupon() || undefined,
    };
    // Embudo: Lead = se envió el formulario de alta (validación OK).
    trackFunnel('Lead');
    signingUp.current = true;
    try {
      await dispatch(registerAsync(payload)).unwrap();
      // El alta deja el tenant en el plan Gratis (sin prueba ni tarjeta): cuenta creada = cuenta activa.
      await trackFunnel('CompleteRegistration', 'StartTrial');
      navigate('/completar-perfil');
    } catch {
      // handled by slice
    } finally {
      signingUp.current = false;
    }
  };

  // Paso 1 Google: guardar el token y pedir nombre de negocio
  const handleGoogle = (idToken: string) => {
    setLocalError('');
    if (!acceptedTerms) {
      setLocalError('Tenés que aceptar los términos y condiciones');
      return;
    }
    setPendingGoogleToken(idToken);
    setCompanyName('');
    setStep('google-company');
  };

  // Paso 2 Google: enviar con el nombre de negocio
  const handleGoogleCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (!companyName.trim() || !subdomain) {
      setLocalError('Ingresá el nombre de tu negocio');
      return;
    }
    // Embudo: Lead = se envió el alta con Google (nombre del negocio).
    trackFunnel('Lead');
    signingUp.current = true;
    try {
      const res = await dispatch(
        googleAuthAsync({
          googleToken: pendingGoogleToken,
          companyName: companyName.trim(),
          subdomain,
          plan: 'Growth',
        }),
      ).unwrap();
      // Si el email de Google ya tenía cuenta, el backend lo loguea en vez de crearla; la respuesta
      // no lo dice, así que contamos alta solo si el negocio todavía no completó el onboarding.
      if (!res.tenant?.onboardingCompletedAt) {
        await trackFunnel('CompleteRegistration', 'StartTrial');
      }
      navigate('/completar-perfil');
    } catch {
      // handled by slice
    } finally {
      signingUp.current = false;
    }
  };

  const bgUrl = authBgUrl(AUTH_BG_UNSPLASH_IDS[0]);
  const alertMsg = localError || error;

  const fieldSx = {
    fontFamily: INTER_STACK,
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      fontFamily: INTER_STACK,
      bgcolor: '#fff',
      '& fieldset': {
        borderColor: 'rgba(15,23,42,0.18)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(15,23,42,0.32)',
      },
      '&.Mui-focused fieldset': {
        borderColor: INDIGO,
        borderWidth: 1.5,
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: INTER_STACK,
      color: INK_SOFT,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: INDIGO,
    },
    '& .MuiFormHelperText-root': {
      fontFamily: INTER_STACK,
      color: INK_SOFT,
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        fontFamily: INTER_STACK,
        bgcolor: '#ffffff',
      }}
    >
      {isDesktop && (
        <LeftPanel
          kicker="LOTE · REGISTER"
          headline={
            <>
              Dejá de sobrevender.
              <br />
              Dejá de contar{' '}
              <HighlightedWord>a&nbsp;mano</HighlightedWord>.
            </>
          }
          subtitle={AUTH_SUBTITLE}
          testimonials={AUTH_TESTIMONIALS}
          bgUrl={bgUrl}
          reducedMotion={reducedMotion}
        />
      )}

      <Box
        component={motion.div}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45, delay: 0.6, ease: 'easeOut' }}
        sx={{
          position: 'relative',
          flex: 1,
          width: { xs: '100%', md: '50%' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2.5, sm: 5, md: 7, lg: 10 },
          py: { xs: 5, md: 6 },
          bgcolor: '#ffffff',
          '&::before': isDesktop
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '1px',
                backgroundImage: `repeating-linear-gradient(
                  to bottom,
                  ${INDIGO} 0,
                  ${INDIGO} 2px,
                  transparent 2px,
                  transparent 8px
                )`,
                opacity: 0.18,
              }
            : undefined,
        }}
      >
        {!isDesktop && (
          <Box sx={{ alignSelf: 'flex-start', mb: 4 }}>
            <BrandMark color={INK} size="md" />
          </Box>
        )}

        <Box sx={{ width: '100%', maxWidth: 440 }}>
          <Box sx={{ mb: 2 }}>
            <SkuKicker label="LOTE · SIGNUP-001" />
          </Box>

          {/* "Price tag" del plan Gratis — part of the ticket language */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1.75,
              px: 1,
              py: 0.5,
              bgcolor: YELLOW,
              color: INK,
              border: `1px solid ${INK}`,
              borderRadius: '2px',
              fontFamily: MONO_STACK,
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: CREAM,
                border: `1px solid ${INK}`,
                display: 'inline-block',
              }}
            />
            Gratis para siempre · sin tarjeta
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: INTER_STACK,
              fontSize: { xs: 26, sm: 30 },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: INK,
              mb: 1,
            }}
          >
            Creá tu <HighlightedWord>cuenta</HighlightedWord>.
          </Typography>
          <Typography
            sx={{
              fontFamily: INTER_STACK,
              fontSize: 14.5,
              color: INK_SOFT,
              mb: 3,
            }}
          >
            Llevá tu inventario al siguiente nivel.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: MONO_STACK,
              fontSize: 10.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: INK,
              opacity: 0.5,
              mb: 1.5,
            }}
          >
            <Box component="span">Doc · Signup</Box>
            <Box component="span">{new Date().toISOString().slice(0, 10)}</Box>
          </Box>
          <DottedRule my={0} />

          {alertMsg && (
            <Alert
              severity="error"
              sx={{
                mt: 2.5,
                mb: 0,
                fontFamily: INTER_STACK,
                border: '1px solid rgba(220,38,38,0.25)',
                borderRadius: '5px',
              }}
            >
              {alertMsg}
            </Alert>
          )}

          {step === 'google-company' ? (
            /* ── Paso 2 Google: solo nombre del negocio ── */
            <Box component="form" onSubmit={handleGoogleCompanySubmit} noValidate>
              <Typography
                sx={{
                  mt: 2.5,
                  mb: 0.5,
                  fontFamily: INTER_STACK,
                  fontSize: 14,
                  color: INK_SOFT,
                }}
              >
                ¡Casi listo! ¿Cómo se llama tu negocio?
              </Typography>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <TextField
                  fullWidth
                  label="Nombre del negocio"
                  name="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  autoFocus
                  placeholder="Moda La Prov"
                  autoComplete="organization"
                  sx={{ mt: 2, mb: 1.5, ...fieldSx }}
                />
                {subdomain && (
                  <Typography
                    sx={{
                      fontFamily: MONO_STACK,
                      fontSize: 11,
                      color: INK_SOFT,
                      mb: 2.5,
                      opacity: 0.7,
                    }}
                  >
                    Tu URL: {subdomain}.dashflow.com.ar
                  </Typography>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  disableElevation
                  sx={{
                    py: 1.4,
                    borderRadius: '5px',
                    fontFamily: INTER_STACK,
                    fontWeight: 600,
                    fontSize: 15,
                    textTransform: 'none',
                    letterSpacing: 0,
                    bgcolor: INDIGO,
                    color: '#fff',
                    boxShadow: '0 1px 0 rgba(15,23,42,0.08)',
                    '&:hover': {
                      bgcolor: INDIGO_DARK,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(30,64,175,0.22)',
                    },
                    '&:active': { transform: 'translateY(0)' },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: '#fff' }} />
                  ) : (
                    'Continuar →'
                  )}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => { setStep('main'); setLocalError(''); dispatch(clearError()); }}
                  sx={{
                    mt: 1.5,
                    fontFamily: INTER_STACK,
                    fontSize: 13,
                    color: INK_SOFT,
                    textTransform: 'none',
                  }}
                >
                  ← Volver
                </Button>
              </motion.div>
            </Box>
          ) : (
            <>
              {/* ── Paso 1: Google FIRST ── */}
              <motion.div
                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
              >
                <Box sx={{ mt: 2.5 }}>
                  <GoogleSignInButton
                    text="signup_with"
                    onSuccess={handleGoogle}
                    onError={(msg) => setLocalError(msg)}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      sx={{
                        color: 'rgba(15,23,42,0.4)',
                        borderRadius: 0,
                        '& .MuiSvgIcon-root': { fontSize: 20 },
                        '&.Mui-checked': { color: INDIGO },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: INTER_STACK,
                        fontSize: 13.5,
                        color: INK_SOFT,
                      }}
                    >
                      Acepto los{' '}
                      <Link
                        href="/terminos"
                        target="_blank"
                        sx={{
                          color: INDIGO,
                          fontWeight: 600,
                          borderBottom: `1px dotted ${INDIGO}`,
                          textDecoration: 'none',
                        }}
                      >
                        términos y condiciones
                      </Link>
                    </Typography>
                  }
                  sx={{ mt: 1.5, mb: 0, mx: 0, alignItems: 'flex-start' }}
                />
              </motion.div>

              <DottedRule label="o con email" my={2.5} />

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                  animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.82 }}
                >
                  <TextField
                    fullWidth
                    label="Nombre del negocio"
                    name="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    placeholder="Moda La Prov"
                    autoComplete="organization"
                    sx={{ mb: 2, ...fieldSx }}
                  />
                </motion.div>

                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                  animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.9 }}
                >
                  <TextField
                    fullWidth
                    label="Tu email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    sx={{ mb: 2, ...fieldSx }}
                  />
                </motion.div>

                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                  animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.98 }}
                >
                  <TextField
                    fullWidth
                    label="Tu contraseña"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    helperText="Mínimo 6 caracteres"
                    sx={{ mb: 2, ...fieldSx }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((v) => !v)}
                            edge="end"
                            aria-label="Mostrar contraseña"
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityOff sx={{ fontSize: 20 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 20 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                  animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1.06 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        sx={{
                          color: 'rgba(15,23,42,0.4)',
                          borderRadius: 0,
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          '&.Mui-checked': { color: INDIGO },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: INTER_STACK,
                          fontSize: 13.5,
                          color: INK_SOFT,
                        }}
                      >
                        Acepto los{' '}
                        <Link
                          href="/terminos"
                          target="_blank"
                          sx={{
                            color: INDIGO,
                            fontWeight: 600,
                            borderBottom: `1px dotted ${INDIGO}`,
                            textDecoration: 'none',
                          }}
                        >
                          términos y condiciones
                        </Link>
                      </Typography>
                    }
                    sx={{ mb: 2.5, mx: 0, alignItems: 'flex-start' }}
                  />
                </motion.div>

                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                  animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1.12 }}
                >
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    disableElevation
                    sx={{
                      py: 1.4,
                      borderRadius: '5px',
                      fontFamily: INTER_STACK,
                      fontWeight: 600,
                      fontSize: 15,
                      textTransform: 'none',
                      letterSpacing: 0,
                      bgcolor: INDIGO,
                      color: '#fff',
                      boxShadow: '0 1px 0 rgba(15,23,42,0.08)',
                      transition:
                        'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                      '&:hover': {
                        bgcolor: INDIGO_DARK,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 16px rgba(30,64,175,0.22)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} sx={{ color: '#fff' }} />
                    ) : (
                      'Crear cuenta'
                    )}
                  </Button>
                </motion.div>

                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 1.2 }}
                >
                  <Box
                    sx={{
                      mt: 3,
                      height: '1px',
                      backgroundImage: `repeating-linear-gradient(to right, ${INK} 0, ${INK} 2px, transparent 2px, transparent 8px)`,
                      opacity: 0.25,
                    }}
                  />
                  <Typography
                    sx={{
                      mt: 2,
                      textAlign: 'center',
                      fontFamily: INTER_STACK,
                      fontSize: 14,
                      color: INK_SOFT,
                    }}
                  >
                    ¿Ya tenés cuenta?{' '}
                    <Link
                      component={RouterLink}
                      to="/login"
                      sx={{
                        fontFamily: INTER_STACK,
                        color: INDIGO,
                        fontWeight: 600,
                        textDecoration: 'none',
                        borderBottom: `1px dotted ${INDIGO}`,
                        pb: '1px',
                        '&:hover': { borderBottomStyle: 'solid' },
                      }}
                    >
                      Iniciá sesión →
                    </Link>
                  </Typography>
                </motion.div>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
